import { useMemo } from 'react';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

const LOCALES: Record<string, string> = {
  es: 'es-CL',
  en: 'en-US',
  zh: 'zh-CN',
};

// Formatos de números y fechas del reporte según el idioma elegido.
export const useReportFormat = () => {
  const { t, currentLanguage } = useTranslation('reports');
  const locale = LOCALES[currentLanguage] || LOCALES.es;

  return useMemo(() => {
    const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const percent = new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 0,
    });
    const day = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
    const monthName = new Intl.DateTimeFormat(locale, {
      month: 'short',
      timeZone: 'UTC',
    });
    const weekday = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      timeZone: 'UTC',
    });
    // Fecha de un instante (ISO) en la zona del navegador, sin hora.
    const localDay = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const dateTime = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      t,
      locale,
      number: (value: number) => integer.format(value),
      // Singular o plural según la cantidad: plural(1, '{{n}} embarque', '{{n}} embarques').
      plural: (count: number, one: string, many: string) =>
        t(count === 1 ? one : many, { n: integer.format(count) }),
      percent: (value: number) =>
        percent.format(Number.isFinite(value) ? value : 0),
      // Fechas YYYY-MM-DD, sin corrimiento por zona horaria.
      date: (value: string | null | undefined) =>
        value ? day.format(new Date(`${value.slice(0, 10)}T00:00:00Z`)) : '—',
      // Rango de días: "12–18 oct", o "29 sept – 5 oct" si cruza de mes.
      dayRange: (start: string, end: string) => {
        const parts = (value: string) => {
          const day = new Date(`${value.slice(0, 10)}T00:00:00Z`);
          return {
            day: day.getUTCDate(),
            month: monthName.format(day).replace('.', ''),
          };
        };
        const from = parts(start);
        const to = parts(end);
        return from.month === to.month
          ? `${from.day}–${to.day} ${to.month}`
          : `${from.day} ${from.month} – ${to.day} ${to.month}`;
      },
      // Día de la semana y día del mes: { weekday: "mar", day: "21 oct" }.
      weekday: (value: string) => {
        const day = new Date(`${value.slice(0, 10)}T00:00:00Z`);
        return {
          weekday: weekday.format(day).replace('.', ''),
          day: `${day.getUTCDate()} ${monthName.format(day).replace('.', '')}`,
        };
      },
      localDate: (value: string | null | undefined) =>
        value ? localDay.format(new Date(value)) : '—',
      dateTime: (value: string | null | undefined) =>
        value ? dateTime.format(new Date(value)) : '—',
    };
  }, [locale, t]);
};
